import { useMemo, useState, type FormEvent } from 'react';
import {
  commissionApi,
  peopleApi,
  type CommissionBand,
  type CommissionPlan,
  type CommissionPlanDetail,
  type Person,
  type SalesAgentSummary,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useConfirm, useToast } from '@/components/Feedback';
import { formatDateTime, formatPeso, humanize } from '@/lib/format';
import { adminNavItems } from '@/pages/navItems';

/** `rate` is held as a percentage string in the UI ("5" = 5%); the API stores decimals (0.05). */
type BandDraft = { fromAmount: string; toAmount: string; rate: string };

/** API decimal ("0.05") → UI percent ("5"). */
function rateToPercent(rate: string | null | undefined): string {
  if (rate === null || rate === undefined || rate === '') return '';
  const n = Number(rate);
  return Number.isFinite(n) ? String(Number((n * 100).toFixed(4))) : '';
}

/** UI percent ("5") → API decimal ("0.05"). */
function percentToRate(percent: string): string {
  const n = Number(percent);
  return Number.isFinite(n) ? String(Number((n / 100).toFixed(6))) : percent;
}

function formatPercent(rate: string | null | undefined): string {
  const pct = rateToPercent(rate);
  return pct ? `${pct}%` : '—';
}

const METRIC_LABELS: Record<string, string> = {
  trip_count: 'Trips',
  revenue: 'Revenue',
  gross_profit: 'Gross profit',
};

function BandRowInputs({
  index,
  band,
  onChange,
  idPrefix,
  toHint = 'blank = ∞',
}: {
  index: number;
  band: BandDraft;
  onChange: (next: BandDraft) => void;
  idPrefix: string;
  toHint?: string;
}) {
  const n = index + 1;
  return (
    <div className="row" style={{ gap: '0.5rem', marginBottom: '0.5rem' }} role="group" aria-label={`Band ${n}`}>
      <Input
        id={`${idPrefix}-${n}-from`}
        label={`Band ${n} from (₱)`}
        type="number"
        inputMode="decimal"
        min="0"
        value={band.fromAmount}
        onChange={(e) => onChange({ ...band, fromAmount: e.target.value })}
      />
      <Input
        id={`${idPrefix}-${n}-to`}
        label={`Band ${n} to (₱, ${toHint})`}
        type="number"
        inputMode="decimal"
        min="0"
        value={band.toAmount}
        onChange={(e) => onChange({ ...band, toAmount: e.target.value })}
      />
      <Input
        id={`${idPrefix}-${n}-rate`}
        label={`Band ${n} rate (%)`}
        type="number"
        inputMode="decimal"
        min="0"
        max="100"
        step="any"
        value={band.rate}
        onChange={(e) => onChange({ ...band, rate: e.target.value })}
      />
    </div>
  );
}

const DEFAULT_BANDS: BandDraft[] = [
  { fromAmount: '0', toAmount: '100000', rate: '5' },
  { fromAmount: '100000', toAmount: '300000', rate: '7' },
  { fromAmount: '300000', toAmount: '', rate: '10' },
];

function formatBand(band: CommissionBand) {
  const to = band.toAmount ? formatPeso(band.toAmount) : 'and above';
  return `${formatPeso(band.fromAmount)} – ${to} @ ${formatPercent(band.rate)}`;
}

function toApiBands(bands: BandDraft[]): CommissionBand[] {
  return bands.map((b, i) => ({
    fromAmount: b.fromAmount,
    toAmount: b.toAmount.trim() ? b.toAmount : undefined,
    rate: percentToRate(b.rate),
    sortOrder: i + 1,
  }));
}

export function AdminCommissionPlans() {
  const confirm = useConfirm();
  const toast = useToast();
  const { data: plans, loading, error: plansLoadError, reload } = useAsync(() => commissionApi.listPlans(), []);
  const { data: agents, error: agentsLoadError, reload: reloadAgents } = useAsync(() => commissionApi.listAgents(), []);
  const { data: people } = useAsync(() => peopleApi.list(), []);

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [structure, setStructure] = useState<'tiered' | 'flat'>('tiered');
  const [flatRate, setFlatRate] = useState('5');
  const [payOnCollection, setPayOnCollection] = useState(true);
  const [bands, setBands] = useState<BandDraft[]>(DEFAULT_BANDS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CommissionPlanDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const [versionBands, setVersionBands] = useState<BandDraft[]>(DEFAULT_BANDS);
  const [showVersion, setShowVersion] = useState(false);

  const [assignAgentId, setAssignAgentId] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [showNewAgent, setShowNewAgent] = useState(false);
  const [newAgentPersonId, setNewAgentPersonId] = useState('');
  const [newAgentEngagement, setNewAgentEngagement] =
    useState<SalesAgentSummary['engagementLevel']>('full_time');

  const peopleOptions = useMemo(() => {
    const agentPersonIds = new Set((agents ?? []).map((a) => a.personId));
    return ((people ?? []) as Person[]).filter((p) => p.id && !agentPersonIds.has(p.id));
  }, [people, agents]);

  const loadDetail = async (id: string) => {
    setDetailId(id);
    setDetailLoading(true);
    setDetailError('');
    try {
      const d = await commissionApi.getPlan(id);
      setDetail(d);
      setVersionBands(
        d.bands.length
          ? d.bands.map((b) => ({
              fromAmount: b.fromAmount,
              toAmount: b.toAmount ?? '',
              rate: rateToPercent(b.rate),
            }))
          : DEFAULT_BANDS,
      );
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Failed to load plan');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setDetailError('');
    setShowVersion(false);
    setShowAssign(false);
    setShowNewAgent(false);
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await commissionApi.createPlan({
        name,
        structure,
        flatRate: structure === 'flat' ? percentToRate(flatRate) : undefined,
        payOnCollection,
        floorRule: true,
        bonusEligible: true,
        bands: structure === 'tiered' ? toApiBands(bands) : undefined,
      });
      toast(`Draft plan “${created.name ?? name}” created — activate it when ready`);
      setShowAdd(false);
      setName('');
      setBands(DEFAULT_BANDS);
      reload();
      await loadDetail(created.id);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!detailId || !detail) return;
    const otherActive = (plans ?? []).filter(
      (p) => p.name === detail.plan.name && p.status === 'active' && p.id !== detailId,
    );
    const ok = await confirm({
      title: 'Activate plan version?',
      message:
        otherActive.length > 0
          ? `Activate “${detail.plan.name}” v${detail.plan.version}? It replaces the current active version (${otherActive
              .map((p) => `v${p.version}`)
              .join(', ')}), which will be archived. Activated versions can no longer be edited.`
          : `Activate “${detail.plan.name}” v${detail.plan.version}? Agents can then be assigned to it. Activated versions can no longer be edited.`,
      confirmLabel: 'Activate',
    });
    if (!ok) return;
    setActionBusy(true);
    setDetailError('');
    try {
      const res = await commissionApi.activatePlan(detailId);
      toast(
        `“${detail.plan.name}” v${detail.plan.version} is now active${
          res.archivedPriorVersions?.length ? `; ${res.archivedPriorVersions.length} older version(s) archived` : ''
        }`,
      );
      await Promise.all([reload(), loadDetail(detailId), reloadAgents()]);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Activate failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!detailId || !detail) return;
    const onPlan = (agents ?? []).filter((a) => a.commissionPlanId === detailId).length;
    const ok = await confirm({
      title: 'Archive plan version?',
      message: `Archive “${detail.plan.name}” v${detail.plan.version}?${
        onPlan ? ` ${onPlan} agent(s) are assigned to it; their past commission entries are kept.` : ''
      } Archived versions cannot be reactivated.`,
      confirmLabel: 'Archive',
      danger: true,
    });
    if (!ok) return;
    setActionBusy(true);
    setDetailError('');
    try {
      await commissionApi.archivePlan(detailId);
      toast(`“${detail.plan.name}” v${detail.plan.version} archived`);
      await Promise.all([reload(), loadDetail(detailId)]);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Archive failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCreateVersion = async (e: FormEvent) => {
    e.preventDefault();
    if (!detailId || !detail) return;
    setActionBusy(true);
    setDetailError('');
    try {
      const next = await commissionApi.createVersion(detailId, {
        structure: detail.plan.structure,
        flatRate: detail.plan.flatRate ?? undefined,
        payOnCollection: detail.plan.payOnCollection,
        floorRule: detail.plan.floorRule,
        bonusEligible: detail.plan.bonusEligible,
        bands: detail.plan.structure === 'tiered' ? toApiBands(versionBands) : undefined,
      });
      setShowVersion(false);
      toast(`Draft version v${next.version ?? ''} created`.trim());
      reload();
      await loadDetail(next.id);
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Version create failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleAssign = async (e: FormEvent) => {
    e.preventDefault();
    if (!detailId || !assignAgentId) return;
    setActionBusy(true);
    setDetailError('');
    try {
      await commissionApi.assignAgentPlanById(assignAgentId, detailId);
      toast(`${(agents ?? []).find((a) => a.id === assignAgentId)?.personName ?? 'Agent'} assigned to this plan`);
      setShowAssign(false);
      setAssignAgentId('');
      await reloadAgents();
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Assign failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handleNewAgent = async (e: FormEvent) => {
    e.preventDefault();
    if (!detailId || !newAgentPersonId) return;
    setActionBusy(true);
    setDetailError('');
    try {
      await commissionApi.assignAgentPlan({
        personId: newAgentPersonId,
        engagementLevel: newAgentEngagement,
        commissionPlanId: detail?.plan.status === 'active' ? detailId : undefined,
        startDate: new Date().toISOString().slice(0, 10),
      });
      toast(`${peopleOptions.find((p) => p.id === newAgentPersonId)?.fullName ?? 'Agent'} registered as a sales agent`);
      setShowNewAgent(false);
      setNewAgentPersonId('');
      await reloadAgents();
    } catch (err) {
      setDetailError((err as { message?: string }).message ?? 'Create agent failed');
    } finally {
      setActionBusy(false);
    }
  };

  const assignedAgents = (agents ?? []).filter((a) => a.commissionPlanId === detailId);

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Commission plans</h1>
            <p className="text-muted">Create a draft plan, activate it, then assign sales agents. Changes are made as new versions.</p>
          </div>
          <Button type="button" onClick={() => { setError(''); setShowAdd(true); }}>New plan</Button>
        </div>

        {plansLoadError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
            Could not load commission plans: {plansLoadError}{' '}
            <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Retry</Button>
          </p>
        )}
        {agentsLoadError && (
          <p className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
            Could not load agents: {agentsLoadError}{' '}
            <Button type="button" size="sm" variant="secondary" onClick={() => reloadAgents()}>Retry</Button>
          </p>
        )}

        <DataTable
          columns={[
            { key: 'name', header: 'Name' },
            {
              key: 'version',
              header: 'Version',
              render: (r: CommissionPlan) => `v${r.version ?? 1}`,
            },
            {
              key: 'structure',
              header: 'Structure',
              render: (r: CommissionPlan) =>
                r.structure === 'flat' ? `Flat ${formatPercent(r.flatRate)}` : humanize(r.structure),
            },
            {
              key: 'status',
              header: 'Status',
              render: (r: CommissionPlan) => <StatusBadge status={r.status ?? 'draft'} size="sm" />,
            },
            {
              key: 'payOnCollection',
              header: 'Pay on collection',
              render: (r: CommissionPlan) => (r.payOnCollection ? 'Yes' : 'No'),
            },
            {
              key: 'actions',
              header: '',
              sortable: false,
              render: (r: CommissionPlan) => (
                <Button type="button" size="sm" variant="secondary" onClick={() => loadDetail(r.id)}>
                  Open
                </Button>
              ),
            },
          ]}
          data={plans ?? []}
          keyField="id"
          loading={loading}
          dense
          emptyMessage={
            plansLoadError
              ? 'Commission plans could not be loaded.'
              : 'No commission plans yet. Use “New plan” to create one.'
          }
        />

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New commission plan (draft)" size="lg">
          <form onSubmit={handleCreate}>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Select label="Structure" value={structure} onChange={(e) => setStructure(e.target.value as 'tiered' | 'flat')}>
              <option value="tiered">Tiered (marginal)</option>
              <option value="flat">Flat rate</option>
            </Select>
            {structure === 'flat' && (
              <Input
                label="Flat rate (%)"
                type="number"
                inputMode="decimal"
                min="0"
                max="100"
                step="any"
                value={flatRate}
                onChange={(e) => setFlatRate(e.target.value)}
                required
              />
            )}
            {structure === 'tiered' && (
              <div style={{ marginBottom: '1rem' }}>
                <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                  Marginal bands: each rate applies only to the gross profit inside its band. Leave the last band&apos;s
                  “to” blank for no upper limit.
                </p>
                {bands.map((b, i) => (
                  <BandRowInputs
                    key={i}
                    index={i}
                    band={b}
                    idPrefix="create-band"
                    onChange={(next) => {
                      const copy = [...bands];
                      copy[i] = next;
                      setBands(copy);
                    }}
                  />
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setBands([...bands, { fromAmount: '', toAmount: '', rate: '5' }])}
                >
                  Add band
                </Button>
              </div>
            )}
            <label className="row" style={{ gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                checked={payOnCollection}
                onChange={(e) => setPayOnCollection(e.target.checked)}
              />
              Pay on collection
            </label>
            {error && <p className="form-error">{error}</p>}
            <Button type="submit" loading={saving} fullWidth>
              Create draft
            </Button>
          </form>
        </Modal>

        <Modal
          open={detailId !== null}
          onClose={closeDetail}
          title={detail ? `${detail.plan.name} · v${detail.plan.version}` : 'Plan detail'}
          size="lg"
        >
          {detailLoading && !detail && <p className="text-muted">Loading…</p>}
          {detailError && <p className="form-error">{detailError}</p>}
          {detail && (
            <div>
              <div className="grid-3" style={{ marginBottom: 'var(--space-lg)' }}>
                <div className="stat-block">
                  <span className="stat-value"><StatusBadge status={detail.plan.status} size="sm" /></span>
                  <span className="stat-label">Status</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{humanize(detail.plan.structure)}</span>
                  <span className="stat-label">Structure</span>
                </div>
                <div className="stat-block">
                  <span className="stat-value">{detail.plan.payOnCollection ? 'Yes' : 'No'}</span>
                  <span className="stat-label">Pay on collection</span>
                </div>
              </div>

              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                Effective{' '}
                {detail.plan.effectiveFrom
                  ? formatDateTime(detail.plan.effectiveFrom)
                  : detail.plan.activatedAt
                    ? formatDateTime(detail.plan.activatedAt)
                    : detail.plan.status === 'active' && detail.plan.createdAt
                      ? formatDateTime(detail.plan.createdAt)
                      : '—'}
                {detail.plan.effectiveTo ? ` → ${formatDateTime(detail.plan.effectiveTo)}` : ''}
                {detail.plan.createdByName && (
                  <>
                    {' · Created by '}
                    {detail.plan.createdByName}
                  </>
                )}
                {detail.plan.activatedAt && (
                  <>
                    {' · Activated '}
                    {formatDateTime(detail.plan.activatedAt)}
                    {detail.plan.activatedByName ? ` by ${detail.plan.activatedByName}` : ''}
                  </>
                )}
              </p>

              <h3 style={{ marginTop: 'var(--space-md)' }}>Bands</h3>
              {detail.bands.length ? (
                <ul style={{ margin: '0 0 var(--space-md)', paddingLeft: '1.25rem' }}>
                  {detail.bands.map((b) => (
                    <li key={b.id ?? `${b.fromAmount}-${b.rate}`}>{formatBand(b)}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">
                  {detail.plan.structure === 'flat'
                    ? `Flat rate ${formatPercent(detail.plan.flatRate)}`
                    : 'No bands'}
                </p>
              )}

              <h3>Bonus rules</h3>
              {detail.bonusRules.length ? (
                <ul style={{ margin: '0 0 var(--space-md)', paddingLeft: '1.25rem' }}>
                  {detail.bonusRules.map((r) => (
                    <li key={r.id}>
                      {METRIC_LABELS[r.metric] ?? humanize(r.metric)} ≥{' '}
                      {r.metric === 'trip_count' ? r.threshold : formatPeso(r.threshold)} → {formatPeso(r.amount)}
                      {r.windowDays ? ` within ${r.windowDays} days` : ''}
                      {r.oneOff ? ' (one-off)' : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No bonus rules on this version.</p>
              )}

              <h3>Assigned agents</h3>
              {assignedAgents.length ? (
                <ul style={{ margin: '0 0 var(--space-md)', paddingLeft: '1.25rem' }}>
                  {assignedAgents.map((a) => (
                    <li key={a.id}>
                      {a.personName} · {humanize(a.engagementLevel)}
                      {a.planAssignedAt
                        ? ` · Assigned ${formatDateTime(a.planAssignedAt)}${a.planAssignedByName ? ` by ${a.planAssignedByName}` : ''}`
                        : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No agents assigned to this plan version.</p>
              )}

              <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap', marginTop: 'var(--space-md)' }}>
                {detail.plan.status === 'draft' && (
                  <Button type="button" size="sm" loading={actionBusy} onClick={handleActivate}>
                    Activate
                  </Button>
                )}
                {detail.plan.status !== 'archived' && (
                  <Button type="button" size="sm" variant="secondary" loading={actionBusy} onClick={handleArchive}>
                    Archive
                  </Button>
                )}
                <Button type="button" size="sm" variant="secondary" onClick={() => setShowVersion(true)}>
                  New version
                </Button>
                {detail.plan.status === 'active' && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => setShowAssign(true)}>
                    Assign agent
                  </Button>
                )}
                <Button type="button" size="sm" variant="secondary" onClick={() => setShowNewAgent(true)}>
                  Register agent
                </Button>
              </div>

              {showVersion && (
                <form onSubmit={handleCreateVersion} style={{ marginTop: 'var(--space-lg)' }}>
                  <h3>New draft version (bands)</h3>
                  {versionBands.map((b, i) => (
                    <BandRowInputs
                      key={i}
                      index={i}
                      band={b}
                      idPrefix="version-band"
                      toHint="blank = ∞"
                      onChange={(next) => {
                        const copy = [...versionBands];
                        copy[i] = next;
                        setVersionBands(copy);
                      }}
                    />
                  ))}
                  <div className="row" style={{ gap: '0.5rem' }}>
                    <Button type="submit" size="sm" loading={actionBusy}>Create draft version</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowVersion(false)}>Cancel</Button>
                  </div>
                </form>
              )}

              {showAssign && (
                <form onSubmit={handleAssign} style={{ marginTop: 'var(--space-lg)' }}>
                  <Select
                    label="Existing agent"
                    value={assignAgentId}
                    onChange={(e) => setAssignAgentId(e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {(agents ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.personName} ({humanize(a.engagementLevel)})
                      </option>
                    ))}
                  </Select>
                  <div className="row" style={{ gap: '0.5rem' }}>
                    <Button type="submit" size="sm" loading={actionBusy}>Assign to this plan</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowAssign(false)}>Cancel</Button>
                  </div>
                </form>
              )}

              {showNewAgent && (
                <form onSubmit={handleNewAgent} style={{ marginTop: 'var(--space-lg)' }}>
                  <Select
                    label="Person"
                    value={newAgentPersonId}
                    onChange={(e) => setNewAgentPersonId(e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {peopleOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.fullName ?? 'Unnamed person'}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Engagement"
                    value={newAgentEngagement}
                    onChange={(e) =>
                      setNewAgentEngagement(e.target.value as SalesAgentSummary['engagementLevel'])
                    }
                  >
                    <option value="full_time">Full time</option>
                    <option value="part_time">Part time</option>
                    <option value="commission_only">Commission only</option>
                  </Select>
                  {detail.plan.status !== 'active' && (
                    <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      Plan is not active — agent will be created without a plan assignment until you activate and assign.
                    </p>
                  )}
                  <div className="row" style={{ gap: '0.5rem' }}>
                    <Button type="submit" size="sm" loading={actionBusy}>Register agent</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewAgent(false)}>Cancel</Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
}
